// ── <AccountModal> — Account centered-modal popup ──
//
// Extracted verbatim from AppInner's render (App.jsx ~line 7234) that rendered
// the Account modal: identity (AccountIdentity), optional debug 3-column grid
// (USER ACCOUNT / PERSISTENCE+SAVED STATE / OBSERVABILITY), and a sign-out
// button footer. Gated by `accountOpen` in the caller.
//
// This is a behavior-preserving extraction: ALL logic is ported unchanged from
// App.jsx; only the closure values are re-sourced from props (`ctx`).
//
// The component is GATED — the caller (App.jsx) is responsible for rendering
// this component only when `accountOpen` is true (preserving the exact gating
// and JSX position/z-order from AppInner).
//
// Module-level deps imported directly (same file used `import … from` in App.jsx):
//   C                — lib/colors.js
//   AccountIdentity  — components/AccountIdentity.jsx
//   DrawerHelp       — components/Panels.jsx
//   HelpReopenButton — components/Panels.jsx
//
// Runtime values come from `ctx` (AppInner-scoped at render time):
//   setAccountOpen      — setter to close the modal
//   authEnabled         — bool, whether auth is enabled in this environment
//   me                  — null | {email, name, …} from /api/me
//   debugUI             — bool, debug-UI enabled
//   gcpProject          — string, project for the trace links (useAuthSession)
//   persistenceEnabled  — bool, DB-backed persistence enabled
//   forecasts           — array of forecast runs
//   pinned              — {[id]: bool} pinned forecast map
//   lastObs             — null | {traceId, backend, requestId, latencyMs, ts}
//   lastBugObs          — null | {bugId, requestId, ts}
//   logCounts           — from services/telemetry — () => {ui, trace, streamed}
//   clearLogRings       — from lib/logRingBuffer
//   drawerHelpState     — {account: bool, …} drawer-help visibility
//   setDrawerHelpState  — setter for drawerHelpState

import { postLogout } from "../services/accountApi.js";
import React, { useEffect, useRef } from "react";
import { recentOrigin } from "../lib/telemetry/recentControl.js";
import { emitAccountOpen } from "../lib/telemetry/uiIntents.js";
import { C } from "../lib/colors.js";
import AccountIdentity from "./AccountIdentity.jsx";
import DeployedRevision from "./DeployedRevision.jsx";
import { DrawerHelpToggle, ModalShell, CloseButton } from "./Panels.jsx";
import { copyToClipboard } from "../lib/util/clipboard.js";
import { redirectToLogin } from "../lib/apiClient.js";
import { cloudTraceUrl, cloudLoggingUrl } from "../lib/util/traceLinks.js";
import { useDeployedRevision } from "../hooks/useDeployedRevision.js";
import { RESET_LOG_TITLE, formatLogCounts } from "../lib/telemetry/logCounts.js";
import { beginIntent, ui, withIntent } from "../lib/telemetry/instrument.js";
import { drainTelemetry } from "../services/telemetry.js";
import { Button } from "./ui/Primitives.jsx";
import { IconMessage } from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";
import { bugReportShortcutLabel } from "../lib/bugReportTriggers.js";

export default function AccountModal({ ctx }) {
  // One `account.open` per opening (the modal mounts each time it opens); the ref keeps
  // StrictMode's replayed mount from counting it twice.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emitAccountOpen({ origin: recentOrigin("account.open") });
  }, []);
  const {
    setAccountOpen,
    authEnabled,
    me,
    debugUI,
    gcpProject,
    persistenceEnabled,
    forecasts,
    pinned,
    lastObs,
    lastBugObs,
    logCounts,
    clearLogRings,
    drawerHelpState,
    setDrawerHelpState,
    // Opens the feedback form (and closes this panel).
    openFeedback,
  } = ctx;

  // Deployed-revision metadata (app + notifier). Lazily fetched only for debug
  // users — this modal mounts only when opened, and `debugUI` gates the block.
  const deployedRevision = useDeployedRevision(debugUI);

  // Console deep links for the last observed trace. `gcpProject` arrives via ctx
  // from useAuthSession — NOT from `me` (see that hook's comment). Both builders
  // return null when either half is missing, so a session the server withholds
  // gcpProject from renders no links.
  const traceHref = cloudTraceUrl(lastObs?.traceId, gcpProject);
  const loggingHref = cloudLoggingUrl(lastObs?.traceId, gcpProject);

  // Local re-render trigger: RESET LOG clears the module-level rings, but clearing a
  // singleton doesn't re-render, so bump state to refresh the counts.
  const [, bumpLogCount] = React.useState(0);
  const resetLog = () => {
    try {
      clearLogRings();
    } catch (_e) {
      /* no-op */
    }
    bumpLogCount((n) => n + 1);
  };

  // Debug mode shows the 3-column grid whose OBSERVABILITY column holds full
  // 32-hex trace ids + request UUIDs — widen the modal so those values fit on
  // one line (see the nowrap values below). Non-debug is identity-only → keep it
  // compact (this also leaves the debug-off visual snapshot unchanged).
  return (
    <ModalShell
      onClose={() => setAccountOpen(false)}
      surface="account"
      zIndex={550}
      panelProps={{ "data-account-panel": "", "data-telemetry-redact": "" }}
      panelStyle={{
        boxShadow: C.shadowBox,
        width: debugUI ? "min(900px, 95vw)" : "min(520px, 92vw)",
        maxHeight: "85vh",
        overflowY: "auto",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        marginTop: "8vh",
      }}
    >
      <div style={{ padding: "20px 24px 8px", display: "flex", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C.white, letterSpacing: 0 }}>
          Account
        </h2>
        <div style={{ flex: 1 }} />
        <CloseButton
          onClick={() => setAccountOpen(false)}
          style={{ width: 32, height: 32, borderRadius: 8, fontSize: 18, color: C.gray }}
        />
      </div>
      <DrawerHelpToggle
        k="account"
        corner="bl"
        drawerHelpState={drawerHelpState}
        setDrawerHelpState={setDrawerHelpState}
        boxStyle={{ margin: "4px 24px 8px" }}
      />
      <div style={{ padding: "8px 24px 20px" }}>
        {!authEnabled ? (
          <div style={{ fontSize: 13, color: C.gray, padding: "8px 0" }}>
            Sign-in isn&apos;t set up here, so there is no account to show.
          </div>
        ) : !me ? (
          <div style={{ fontSize: 13, color: C.gray, padding: "8px 0" }}>
            You&apos;re not signed in.
          </div>
        ) : (
          <>
            {debugUI ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr minmax(330px, 1.5fr)",
                  gap: 24,
                  alignItems: "start",
                }}
              >
                {/* Col 1: USER ACCOUNT — identity + Zitadel console link */}
                <div>
                  <div className="account-section-label">USER ACCOUNT</div>
                  <AccountIdentity me={me} />
                </div>
                {/* Col 2: PERSISTENCE + SAVED STATE stacked */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.yellow,
                      letterSpacing: 0.6,
                      marginBottom: 8,
                      fontWeight: "bold",
                    }}
                  >
                    PERSISTENCE
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: persistenceEnabled ? C.green : C.gray,
                        display: "inline-block",
                      }}
                    />
                    <span style={{ fontSize: 12, color: C.white, fontWeight: "bold" }}>
                      {persistenceEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.gray, lineHeight: 1.5 }}>
                    {persistenceEnabled
                      ? "Forecasts and pins sync to your account across devices."
                      : "Browser-only mode — state is lost on reload."}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.yellow,
                      letterSpacing: 0.6,
                      marginTop: 18,
                      marginBottom: 8,
                      fontWeight: "bold",
                    }}
                  >
                    SAVED STATE
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 20, color: C.white, fontWeight: "bold" }}>
                      {forecasts.length}
                    </span>
                    <span style={{ fontSize: 12, color: C.gray, letterSpacing: 0 }}>
                      forecast{forecasts.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 14, color: C.yellow, fontWeight: "bold" }}>
                      {Object.values(pinned).filter(Boolean).length}
                    </span>
                    <span style={{ fontSize: 12, color: C.gray, letterSpacing: 0 }}>pinned</span>
                  </div>
                </div>
                {/* Col 3: OBSERVABILITY (last-call meta) */}
                <div>
                  <div className="account-section-label">OBSERVABILITY</div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "max-content 1fr",
                      rowGap: 6,
                      columnGap: 14,
                      fontSize: 12,
                      color: C.grayL,
                      fontFamily: '"IBM Plex Mono",monospace',
                    }}
                  >
                    <span className="account-observation-key">last trace</span>
                    <span
                      {...(lastObs?.traceId && ui("account.copy-trace-id"))}
                      className="account-observation-value"
                      style={{
                        whiteSpace: "nowrap",
                        cursor: lastObs?.traceId ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (lastObs?.traceId) copyToClipboard(lastObs.traceId);
                      }}
                      title={lastObs?.traceId ? "Click to copy. Use in sooth-sql: /trace <id>" : ""}
                    >
                      {lastObs?.traceId || "—"}
                    </span>
                    {/* Console deep links for the last trace. Rendered only when
                    the server supplied a project (/api/version's gcpProject,
                    EXPOSE_DEBUG_UI-gated) AND we have a trace id — off-GCP local
                    dev has neither, so the row simply doesn't appear. */}
                    {traceHref || loggingHref ? (
                      <>
                        <span className="account-observation-key">open trace</span>
                        <span
                          className="account-observation-value"
                          style={{ display: "flex", gap: 10 }}
                        >
                          {traceHref && (
                            <a
                              {...ui("account.trace-link", "trace")}
                              href={traceHref}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: C.action }}
                              title="Open this trace in Cloud Trace"
                            >
                              Cloud Trace
                            </a>
                          )}
                          {loggingHref && (
                            <a
                              {...ui("account.trace-link", "logs")}
                              href={loggingHref}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: C.action }}
                              title="Open every log line correlated to this trace"
                            >
                              Logs
                            </a>
                          )}
                        </span>
                      </>
                    ) : null}
                    <span className="account-observation-key">last backend</span>
                    <span className="account-observation-value">{lastObs?.backend || "—"}</span>
                    <span className="account-observation-key">last latency</span>
                    <span className="account-observation-value">
                      {lastObs?.latencyMs ? `${(lastObs.latencyMs / 1000).toFixed(2)}s` : "—"}
                    </span>
                    <span className="account-observation-key">last request</span>
                    <span className="account-observation-value" style={{ whiteSpace: "nowrap" }}>
                      {lastObs?.requestId || "—"}
                    </span>
                    <span className="account-observation-key">last bug id</span>
                    <span
                      {...(lastBugObs?.bugId && ui("account.copy-bug-id"))}
                      className="account-observation-value"
                      style={{
                        whiteSpace: "nowrap",
                        cursor: lastBugObs?.bugId ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (lastBugObs?.bugId) copyToClipboard(lastBugObs.bugId);
                      }}
                      title={
                        lastBugObs?.bugId ? "Click to copy. Use in sooth-sql: /bug_report <id>" : ""
                      }
                    >
                      {lastBugObs?.bugId || "—"}
                    </span>
                    <span className="account-observation-key">log buffer</span>
                    <span className="account-observation-value" data-testid="account-log-counts">
                      {formatLogCounts(logCounts())}
                    </span>
                  </div>
                  <button
                    {...ui("account.reset-log")}
                    type="button"
                    onClick={resetLog}
                    title={RESET_LOG_TITLE}
                    style={{
                      marginTop: 12,
                      cursor: "pointer",
                      fontSize: 11,
                      color: C.gray,
                      letterSpacing: 0,
                      padding: "3px 9px",
                      border: `1px solid ${C.border}`,
                      borderRadius: 5,
                      fontFamily: '"IBM Plex Mono",monospace',
                      background: "transparent",
                      appearance: "none",
                      lineHeight: 1.2,
                    }}
                  >
                    RESET LOG
                  </button>
                  <DeployedRevision data={deployedRevision} />
                </div>
              </div>
            ) : (
              <AccountIdentity me={me} />
            )}
            <div
              style={{
                marginTop: 20,
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: C.rowInactive,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.white }}>
                  Help us improve {brandProduct.prose}
                </div>
                <div style={{ fontSize: 12, color: C.gray, marginTop: 2 }}>
                  Report a problem or share an idea with the team.
                </div>
              </div>
              <Button
                size="sm"
                variant="primary"
                icon={<IconMessage />}
                {...ui("account.send-feedback")}
                title={`Shortcut: ${bugReportShortcutLabel()}`}
                onClick={() => openFeedback?.()}
              >
                Report a Bug or Give Feedback
              </Button>
            </div>
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${C.border}`,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              {/* Switch account: forces the identity provider's account picker
                  (prompt=select_account) regardless of the pinned-account hint. Distinct from
                  Sign out: this keeps the session and just re-selects, whereas Sign out is a full
                  logout. */}
              <Button
                variant="ghost"
                size="sm"
                {...ui("account.switch")}
                onClick={() => {
                  withIntent(
                    "account.switch",
                    () => undefined,
                    {},
                    {
                      origin: "account.switch",
                      surface: "account",
                    },
                  );
                  redirectToLogin({ prompt: "select_account" });
                }}
              >
                Switch account
              </Button>
              <Button
                size="sm"
                {...ui("account.sign-out")}
                onClick={async () => {
                  const intent = beginIntent(
                    "account.sign_out",
                    {},
                    { origin: "account.sign-out", surface: "account" },
                  );
                  // Upload what is queued, this start included, while the session still
                  // exists: logout ends it, and the upload after would be refused. Bounded
                  // (drainTelemetry), so a slow or failing upload never holds up the sign-out.
                  // A successful logout's end stays in the tab's backlog; the logout request
                  // carries x-intent-id, so its server log line and span record the outcome.
                  await drainTelemetry();
                  try {
                    const r = await postLogout({ intentId: intent.id });
                    intent.end(r.ok ? "ok" : "error");
                    // A refused logout leaves the session in place: its error end can go now.
                    if (!r.ok) await drainTelemetry();
                    const d = await r.json().catch(() => ({}));
                    window.location.href = d.logoutUrl || "/";
                  } catch (_e) {
                    intent.end("error");
                    await drainTelemetry();
                    window.location.href = "/";
                  }
                }}
              >
                Sign out
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}
